import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Activity,
  AppWindow,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Moon,
  Sun,
  UserCog,
} from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { useTheme } from '@/lib/theme';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/apps', label: 'Apps', icon: AppWindow },
  { to: '/campaigns', label: 'Campaigns', icon: Megaphone },
  { to: '/settings/keys', label: 'API Keys', icon: KeyRound },
  { to: '/settings/profile', label: 'Profile', icon: UserCog },
];

function isActivePath(pathname: string, to: string) {
  if (to === '/dashboard') return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export function AppShell({ children }: { children: ReactNode }) {
  const { tenant, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          <div className="flex h-10 items-center gap-2 px-2 font-semibold tracking-tight">
            <Activity className="size-5 shrink-0 text-primary" aria-hidden />
            <span className="group-data-[collapsible=icon]:hidden">Pulse</span>
          </div>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {NAV.map((item) => {
                  const Icon = item.icon;
                  const active = isActivePath(pathname, item.to);
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.label}
                      >
                        <NavLink to={item.to}>
                          <Icon />
                          <span>{item.label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={toggle}
                tooltip={theme === 'dark' ? 'Light theme' : 'Dark theme'}
              >
                {theme === 'dark' ? <Sun /> : <Moon />}
                <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={logout} tooltip="Logout">
                <LogOut />
                <span>Logout</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <header className="bg-background sticky top-0 z-10 flex h-14 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          {tenant?.email && (
            <span
              className="text-muted-foreground truncate text-sm"
              title={tenant.email}
            >
              {tenant.email}
            </span>
          )}
        </header>
        <div className="flex-1 overflow-x-hidden">
          <div className="mx-auto w-full max-w-6xl p-6">{children}</div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
